import i18n from "i18next";

// Placeholder strings are stored as i18n sentinels so they re-translate at render time. User edits replace the sentinel with literal text.
const I18N_MARKER = "__i18n:";

const marker = (key, opts) =>
  opts ? `${I18N_MARKER}${key}|${JSON.stringify(opts)}` : `${I18N_MARKER}${key}`;

export function resolveI18nDeep(value) {
  if (typeof value === "string") {
    if (!value.startsWith(I18N_MARKER)) return value;
    const rest = value.slice(I18N_MARKER.length);
    const sepIdx = rest.indexOf("|");
    if (sepIdx === -1) return i18n.t(rest);
    const key = rest.slice(0, sepIdx);
    let opts = {};
    try {
      opts = JSON.parse(rest.slice(sepIdx + 1));
    } catch (_) {}
    return i18n.t(key, opts);
  }
  if (Array.isArray(value)) return value.map(resolveI18nDeep);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = resolveI18nDeep(value[k]);
    return out;
  }
  return value;
}

const unescapeHtmlEntities = (s) =>
  s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");

// Decodes box.state.__text (URI-encoded JSON) → object, or null. Boxes whose
// __text was run through the BasicText HTML restyler (forceHtmlCssProperty,
// fixed in EditorShortcuts' align buttons) end up partially re-encoded and
// HTML-escaped; peel extra encoding layers until the JSON parses so those
// boxes render again instead of collapsing to nothing.
export function decodeAtomicText(text) {
  if (typeof text !== "string" || !text) return null;
  // Healthy encodeURIComponent output never contains raw reserved chars; the
  // restyler's encodeURI leaves `: , & ;` bare and its innerHTML escaped & < >.
  const corrupted = /[:,&;]/.test(text);
  let s = text;
  for (let i = 0; i < 5; i++) {
    try {
      s = decodeURIComponent(s);
    } catch (_) {
      return null;
    }
    if (!s.startsWith("{")) return null;
    for (const candidate of corrupted ? [unescapeHtmlEntities(s), s] : [s]) {
      try {
        const parsed = JSON.parse(candidate);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch (_) {}
    }
  }
  return null;
}

export function getAtomicSampleData(type) {
  switch (type) {
    case "heading":
      return { type: "heading", text: marker("SE_sample_heading") };
    case "eyebrow":
      return { type: "eyebrow", text: marker("SE_sample_eyebrow") };
    case "footer":
      return { type: "footer", text: marker("SE_sample_footer") };
    case "bullet-list":
      return {
        type: "bullet-list",
        title: marker("SE_sample_bullet_title"),
        items: [1, 2, 3].map((n) => marker("SE_sample_bullet_point", { n })),
      };
    case "card-grid":
      return {
        type: "card-grid",
        title: marker("SE_sample_card_title"),
        items: [1, 2].map((n) => ({
          title: marker("SE_sample_card_item_title", { n }),
          body: marker("SE_sample_card_item_body", { n }),
        })),
      };
    case "timeline":
      return {
        type: "timeline",
        title: marker("SE_sample_timeline_title"),
        steps: [1, 2].map((n) => ({
          title: marker("SE_sample_timeline_step", { n }),
          body: marker("SE_sample_timeline_step_body"),
        })),
      };
    case "stat-group":
      return {
        type: "stat-group",
        stats: [
          { value: "100", label: marker("SE_sample_stat_label", { n: 1 }) },
          { value: "50%", label: marker("SE_sample_stat_label", { n: 2 }) },
        ],
      };
    case "quote":
      return {
        type: "quote",
        text: marker("SE_sample_quote_text"),
        attribution: marker("SE_sample_quote_author"),
      };
    case "hero":
      return {
        type: "hero",
        heading: marker("SE_sample_hero_heading"),
        body: marker("SE_sample_hero_body"),
      };
    case "two-column":
      return {
        type: "two-column",
        left: { title: marker("SE_sample_2col_left_title"), body: marker("SE_sample_2col_body") },
        right: { title: marker("SE_sample_2col_right_title"), body: marker("SE_sample_2col_body") },
      };
    case "comparison":
      return {
        type: "comparison",
        left: {
          title: marker("SE_sample_comp_option", { letter: "A" }),
          bullets: [1, 2].map((n) => marker("SE_sample_comp_feature", { n })),
        },
        right: {
          title: marker("SE_sample_comp_option", { letter: "B" }),
          bullets: [1, 2].map((n) => marker("SE_sample_comp_feature", { n })),
        },
      };
    case "section":
      return {
        type: "section",
        sectionNumber: "01",
        heading: marker("SE_sample_section_heading"),
        body: marker("SE_sample_section_body"),
      };
    case "divider":
      return { type: "divider" };
    case "badge":
      return { type: "badge", text: marker("SE_sample_badge") };
    case "cta-button":
      return { type: "cta-button", label: marker("SE_sample_cta"), href: "#" };
    case "icon-block":
      return {
        type: "icon-block",
        iconUrl: null,
        title: marker("SE_sample_icon_title"),
        body: marker("SE_sample_icon_body"),
      };
    default:
      return { type };
  }
}

export const SMART_ELEMENT_CONFIGS = [
  // ── Text ──────────────────────────────────────────────────────────────────
  { type: "heading", displayName: "Heading", icon: "title", group: "Text" },
  { type: "eyebrow", displayName: "Eyebrow", icon: "label", group: "Text" },
  { type: "footer", displayName: "Footer", icon: "border_bottom", group: "Text" },
  { type: "quote", displayName: "Quote", icon: "format_quote", group: "Text" },
  // ── Lists ─────────────────────────────────────────────────────────────────
  { type: "bullet-list", displayName: "Bullet List", icon: "list", group: "Lists" },
  { type: "card-grid", displayName: "Card Grid", icon: "view_module", group: "Lists" },
  { type: "timeline", displayName: "Timeline", icon: "linear_scale", group: "Lists" },
  { type: "stat-group", displayName: "Stats", icon: "insert_chart", group: "Lists" },
  // ── Layout ────────────────────────────────────────────────────────────────
  { type: "hero", displayName: "Hero", icon: "crop_landscape", group: "Layout" },
  { type: "section", displayName: "Section", icon: "view_agenda", group: "Layout" },
  { type: "two-column", displayName: "Two Column", icon: "view_column", group: "Layout" },
  { type: "comparison", displayName: "Comparison", icon: "compare", group: "Layout" },
  // ── UI Atoms ──────────────────────────────────────────────────────────────
  { type: "divider", displayName: "Divider", icon: "remove", group: "UI" },
  { type: "badge", displayName: "Badge", icon: "local_offer", group: "UI" },
  { type: "cta-button", displayName: "CTA Button", icon: "call_to_action", group: "UI" },
  { type: "icon-block", displayName: "Icon Block", icon: "widgets", group: "UI" },
];
